/**
 * Converts a hex color code to HSL values (hue, saturation, lightness).
 * Returns a string formatted for Tailwind/CSS variables: "H S% L%" (space separated, no hsl() wrapper).
 * 
 * @param hex - The hex color code (e.g., "#57876b" or "57876b")
 * @returns The HSL string (e.g., "158 35% 42%") or null if invalid
 */
export function hexToHsl(hex: string): string | null {
    // Remove hash if present
    hex = hex.replace(/^#/, '');

    // Parse hex values
    let r: number, g: number, b: number;

    if (hex.length === 3) {
        r = parseInt(hex[0] + hex[0], 16);
        g = parseInt(hex[1] + hex[1], 16);
        b = parseInt(hex[2] + hex[2], 16);
    } else if (hex.length === 6) {
        r = parseInt(hex.substring(0, 2), 16);
        g = parseInt(hex.substring(2, 4), 16);
        b = parseInt(hex.substring(4, 6), 16);
    } else {
        return null;
    }

    // Convert to [0, 1] range
    r /= 255;
    g /= 255;
    b /= 255;

    // Find min/max values
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;

    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
        }

        h /= 6;
    }

    // Round values
    const hDeg = Math.round(h * 360);
    const sPct = Math.round(s * 100);
    const lPct = Math.round(l * 100);

    return `${hDeg} ${sPct}% ${lPct}%`;
}
